/**
 * Best-effort device location grab. Callers must never block their action on
 * a missing location (poor signal on farms is normal), so nothing here ever
 * rejects.
 *
 * getDeviceLocationDetailed resolves { location, reason }: location is
 * { lat, lng, accuracy } or null, and when null the reason is one of
 * 'unsupported' | 'denied' | 'unavailable' | 'timeout'. A high-accuracy fix
 * is attempted first; anything except an outright denial retries with a
 * low-accuracy (cell/wifi-level) attempt — a rough pin beats no pin for an
 * engineer on the road, and wifi-only iPads often cannot do better.
 */
const attemptPosition = (options) => new Promise(resolve => {
  navigator.geolocation.getCurrentPosition(
    position => resolve({
      location: {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      },
      reason: null
    }),
    error => resolve({
      location: null,
      reason: error.code === 1 ? 'denied' : error.code === 3 ? 'timeout' : 'unavailable'
    }),
    options
  );
});

export async function getDeviceLocationDetailed() {
  if (!navigator.geolocation) {
    return { location: null, reason: 'unsupported' };
  }
  const precise = await attemptPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  if (precise.location || precise.reason === 'denied') return precise;
  return attemptPosition({ enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 });
}

export async function getDeviceLocation() {
  return (await getDeviceLocationDetailed()).location;
}

/**
 * Last heartbeat outcome — written by the Layout heartbeat, read by the
 * sidebar clock panel and the Today clock card, so a silently failing
 * location (denied permission, wifi-only iPad, timeout) becomes something
 * an engineer can read out over the phone.
 */
const LOCATION_STATUS_KEY = 'ids-crm-location-status';

export function writeLocationStatus(state, detail = null) {
  try {
    localStorage.setItem(LOCATION_STATUS_KEY, JSON.stringify({ state, detail, at: new Date().toISOString() }));
  } catch {
    // Storage blocked — the status line just stays empty.
  }
}

export function readLocationStatus() {
  try {
    const raw = localStorage.getItem(LOCATION_STATUS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function locationStatusLabel(statusRecord) {
  if (!statusRecord) return null;
  const time = new Date(statusRecord.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  switch (statusRecord.state) {
    case 'sent': return `Location sent ${time}`;
    case 'denied': return 'Location blocked — allow location for this site in the browser settings';
    case 'unavailable': return 'Location unavailable on this device';
    case 'timeout': return `Location timed out ${time} — retries every few minutes`;
    case 'unsupported': return 'This browser cannot share location';
    case 'send_failed':
      return `Location found but not sent ${time}${statusRecord.detail ? ` (${statusRecord.detail})` : ''} — retrying`;
    default: return null;
  }
}

export const ENGINEER_STATUS_ROLES = ['engineering', 'engineer', 'senior_engineer', 'desk_engineer', 'team_leader'];
